import { assertValue } from '@libs/utils';

import type { LoaderOptions } from '../google/loader/googleMapsLoader';
import { GoogleMapsLoader } from '../google/loader/googleMapsLoader';
import { GOOGLE_API_KEY } from '../helpers/constants';

import { GoogleAddressComponentTypes } from './types';
import type { Location, LocationCriteria } from './types';
import { getComponentLongName, getGeocodeRequest } from './helpers';

export class GeocodingService {
  private loadedService: Promise<google.maps.Geocoder>;

  constructor(language: LoaderOptions['language'] = 'en') {
    this.loadedService = new Promise((resolve, reject) => {
      const loader = new GoogleMapsLoader(GOOGLE_API_KEY, {
        language,
      });

      loader
        .loadLibrary('geocoding')
        .then(({ Geocoder }) => resolve(new Geocoder()))
        .catch((error) => reject(error));
    });
  }

  public async getLocation(criteria: LocationCriteria): Promise<Location> {
    const geocodeRequest = getGeocodeRequest(criteria);

    const service = await this.loadedService;
    return new Promise<Location>((resolve, reject) => {
      service.geocode(geocodeRequest, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results) {
          const firstLocation = results[0];
          assertValue(
            firstLocation,
            'geocodingService.ts > getLocation > firstLocation'
          );
          const addressComponents = firstLocation.address_components;

          const route = getComponentLongName(addressComponents, [
            GoogleAddressComponentTypes.Route,
          ]);
          const streetNumber = getComponentLongName(addressComponents, [
            GoogleAddressComponentTypes.StreetNumber,
          ]);
          const locality = getComponentLongName(addressComponents, [
            GoogleAddressComponentTypes.Locality,
          ]);
          const subLocality = getComponentLongName(addressComponents, [
            GoogleAddressComponentTypes.SubLocality,
            GoogleAddressComponentTypes.SubLocalityLevel,
          ]);
          const postalTown = getComponentLongName(addressComponents, [
            GoogleAddressComponentTypes.PostalTown,
          ]);
          const country = getComponentLongName(addressComponents, [
            GoogleAddressComponentTypes.Country,
          ]);
          const postalCode = getComponentLongName(addressComponents, [
            GoogleAddressComponentTypes.PostalCode,
          ]);

          resolve({
            formattedAddress: firstLocation.formatted_address,
            route,
            country,
            streetNumber,
            locality,
            subLocality,
            postalTown,
            postalCode,
            position: {
              latitude: firstLocation.geometry.location.lat(),
              longitude: firstLocation.geometry.location.lng(),
            },
          });
        } else {
          reject(
            new Error(
              `Geocode was not successful for the following reason: ${status}`
            )
          );
        }
      });
    });
  }
}
